/**
 * Wokelo Caching Layer
 * 
 * Schema:
 * - newsStories: { [date: YYYY-MM-DD]: Story[] } - Last 30 days
 * - companyNews: { [domain]: { articles: Article[], fetchedAt: ISO } }
 * - lastRefreshed: { newsStories: ISO, companyNews: { [domain]: ISO } }
 * - searchedCompanies: { name, domain, addedAt }[]
 */

const CACHE_KEYS = {
  NEWS_STORIES: 'wokelo_news_stories',
  COMPANY_NEWS: 'wokelo_company_news',
  LAST_REFRESHED: 'wokelo_last_refreshed',
  SEARCHED_COMPANIES: 'wokelo_searched_companies',
};

const CACHE_TTL_DAYS = 30;

// Helper to get ISO date string
const toDateKey = (date) => {
  const d = new Date(date);
  return d.toISOString().split('T')[0];
};

// Helper to get relative time string
export const getRelativeTime = (isoString) => {
  if (!isoString) return 'Never';
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Purge entries older than 30 days
const purgeOldEntries = (storiesCache) => {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - CACHE_TTL_DAYS);
  const cutoffKey = toDateKey(cutoffDate);
  
  const purged = {};
  Object.keys(storiesCache).forEach(dateKey => {
    if (dateKey >= cutoffKey) {
      purged[dateKey] = storiesCache[dateKey];
    }
  });
  return purged;
};

// ==================== NEWS STORIES CACHE ====================

export const getNewsStoriesCache = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEYS.NEWS_STORIES);
    if (!cached) return {};
    const parsed = JSON.parse(cached);
    return purgeOldEntries(parsed);
  } catch {
    return {};
  }
};

export const setNewsStoriesCache = (dateKey, stories) => {
  // Trim each story to only essential fields to stay within localStorage quota
  const KEEP_FIELDS = [
    'brief_id', 'title', 'snippet', 'description', 'first_seen_at', 'updated_at',
    'source_count', 'article_count', 'newsworthiness_score', 'portfolio_impact',
    'investor_takeaway', 'diligence_area', 'updated_events', 'closed_at',
    'articles', 'source_articles', 'summary_source', 'primary_tag', 'vertical_tag', 'freestyle_tags',
    'sectional_tags', 'image_url',
  ];
  const trimStory = (s) => {
    const t = {};
    KEEP_FIELDS.forEach(k => { if (s[k] !== undefined) t[k] = s[k]; });
    return t;
  };

  // Store all stories — portfolio-impact stories sorted first, then by score
  const trimmed = [...stories]
    .sort((a, b) => {
      const aImpact = (a.portfolio_impact?.length > 0) ? 1 : 0;
      const bImpact = (b.portfolio_impact?.length > 0) ? 1 : 0;
      if (bImpact !== aImpact) return bImpact - aImpact;
      return (b.newsworthiness_score || 0) - (a.newsworthiness_score || 0);
    })
    .map(trimStory);

  try {
    const existing = getNewsStoriesCache();
    existing[dateKey] = trimmed;
    localStorage.setItem(CACHE_KEYS.NEWS_STORIES, JSON.stringify(existing));
    updateLastRefreshed('newsStories');
  } catch (e) {
    // Quota exceeded — retry with only portfolio-impact stories
    try {
      const existing = getNewsStoriesCache();
      existing[dateKey] = trimmed.filter(s => s.portfolio_impact?.length > 0);
      localStorage.setItem(CACHE_KEYS.NEWS_STORIES, JSON.stringify(existing));
      updateLastRefreshed('newsStories');
    } catch {
      console.warn('Cache write failed even with portfolio-only stories');
    }
  }
};

export const getStoriesForDate = (dateKey) => {
  const cache = getNewsStoriesCache();
  return cache[dateKey] || null;
};

export const getAllCachedStories = () => {
  const cache = getNewsStoriesCache();
  const allStories = [];
  Object.values(cache).forEach(stories => {
    allStories.push(...stories);
  });
  // Dedupe by brief_id
  const seen = new Set();
  return allStories.filter(s => {
    if (seen.has(s.brief_id)) return false;
    seen.add(s.brief_id);
    return true;
  });
};

// ==================== COMPANY NEWS CACHE ====================
// Uses per-company keys (wokelo_company_news_{permalink}) to avoid quota issues
// with a shared large JSON blob.

const COMPANY_NEWS_PREFIX = 'wokelo_company_news_';

export const getCompanyNewsCache = () => {
  // Legacy helper — returns old shared-key cache for backward compat
  try {
    const cached = localStorage.getItem(CACHE_KEYS.COMPANY_NEWS);
    return cached ? JSON.parse(cached) : {};
  } catch {
    return {};
  }
};

// Fields needed for article display — scraped_text excluded (too large for localStorage)
const ARTICLE_DISPLAY_FIELDS = ['title', 'url', 'publisher', 'published_date', 'type', 'ai_summary', 'company_name', 'author', 'source'];
const trimArticle = (a) => {
  const t = {};
  ARTICLE_DISPLAY_FIELDS.forEach(k => { if (a[k] !== undefined) t[k] = a[k]; });
  return t;
};

export const setCompanyNewsCache = (permalink, articles, reportId = null, companyName = null) => {
  const key = COMPANY_NEWS_PREFIX + permalink;
  const trimmed = articles.map(trimArticle);
  const entry = {
    articles: trimmed,
    reportId: reportId !== null ? parseInt(reportId, 10) : null,
    report_id: reportId !== null ? parseInt(reportId, 10) : null,
    companyName: companyName || permalink,
    permalink,
    fetchedAt: new Date().toISOString(),
  };
  // Try storing all trimmed articles; on quota error, retry with 200
  try {
    localStorage.setItem(key, JSON.stringify(entry));
  } catch {
    try {
      localStorage.setItem(key, JSON.stringify({ ...entry, articles: trimmed.slice(0, 200) }));
    } catch {
      console.warn('Company news cache write failed for', permalink);
    }
  }
  updateLastRefreshed('companyNews', permalink);
};

export const getCompanyNews = (permalink) => {
  // 1. Try new per-company key
  try {
    const v = localStorage.getItem(COMPANY_NEWS_PREFIX + permalink);
    if (v) return JSON.parse(v);
  } catch {}
  // 2. Fallback: old shared key (backwards compat)
  try {
    const cache = getCompanyNewsCache();
    if (cache[permalink]) return cache[permalink];
  } catch {}
  return null;
};

// ==================== SEARCHED COMPANIES ====================

export const getSearchedCompanies = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEYS.SEARCHED_COMPANIES);
    return cached ? JSON.parse(cached) : [];
  } catch {
    return [];
  }
};

// Derive permalink from company name for cache keys
export const derivePermalink = (companyName) => {
  return companyName.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
};

// Add a searched company with status tracking
// status: "queued" | "processing" | "completed" | "error" | "no_data_found"
export const addSearchedCompany = (name, permalink, domain, reportId = null, status = "queued") => {
  try {
    const existing = getSearchedCompanies();
    const key = permalink || domain;
    // Don't add duplicates - but update if already exists
    const existingIndex = existing.findIndex(c => (c.permalink || c.domain) === key);
    if (existingIndex >= 0) {
      // Update existing entry
      existing[existingIndex] = {
        ...existing[existingIndex],
        name,
        permalink: permalink || domain,
        domain: domain || permalink,
        reportId: reportId || existing[existingIndex].reportId,
        status: status,
      };
    } else {
      existing.push({
        name,
        permalink: permalink || domain,
        domain: domain || permalink,
        addedAt: new Date().toISOString(),
        reportId,
        status,
      });
    }
    localStorage.setItem(CACHE_KEYS.SEARCHED_COMPANIES, JSON.stringify(existing));
  } catch (e) {
    console.warn('Cache write failed:', e);
  }
};

// Update status and reportId for a searched company
export const updateSearchedCompanyStatus = (permalink, status, reportId = null) => {
  try {
    const existing = getSearchedCompanies();
    const index = existing.findIndex(c => (c.permalink || c.domain) === permalink);
    if (index >= 0) {
      existing[index].status = status;
      if (reportId) existing[index].reportId = reportId;
      localStorage.setItem(CACHE_KEYS.SEARCHED_COMPANIES, JSON.stringify(existing));
    }
  } catch (e) {
    console.warn('Cache update failed:', e);
  }
};

// Get a specific searched company by permalink
export const getSearchedCompany = (permalink) => {
  const companies = getSearchedCompanies();
  return companies.find(c => (c.permalink || c.domain) === permalink) || null;
};

// Clear report_id and reset status to idle for a company (used on Refresh)
export const clearSearchedCompanyReport = (permalink) => {
  try {
    const existing = getSearchedCompanies();
    const index = existing.findIndex(c => (c.permalink || c.domain) === permalink);
    if (index >= 0) {
      existing[index].status = "idle";
      existing[index].reportId = null;
      localStorage.setItem(CACHE_KEYS.SEARCHED_COMPANIES, JSON.stringify(existing));
    }
  } catch (e) {
    console.warn('Cache update failed:', e);
  }
};

export const removeSearchedCompany = (permalink) => {
  try {
    const existing = getSearchedCompanies();
    const filtered = existing.filter(c => (c.permalink || c.domain) !== permalink);
    localStorage.setItem(CACHE_KEYS.SEARCHED_COMPANIES, JSON.stringify(filtered));
  } catch (e) {
    console.warn('Cache write failed:', e);
  }
};

// Clear company news cache for a specific permalink
export const clearCompanyNewsCacheEntry = (permalink) => {
  // Remove per-company key
  try { localStorage.removeItem(COMPANY_NEWS_PREFIX + permalink); } catch {}
  // Also clean up legacy shared cache entry
  try {
    const existing = getCompanyNewsCache();
    if (existing[permalink]) {
      delete existing[permalink];
      localStorage.setItem(CACHE_KEYS.COMPANY_NEWS, JSON.stringify(existing));
    }
  } catch (e) {
    console.warn('Cache clear failed:', e);
  }
};

// ==================== LAST REFRESHED ====================

export const getLastRefreshed = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEYS.LAST_REFRESHED);
    return cached ? JSON.parse(cached) : { newsStories: null, companyNews: {} };
  } catch {
    return { newsStories: null, companyNews: {} };
  }
};

const updateLastRefreshed = (type, domain = null) => {
  try {
    const existing = getLastRefreshed();
    if (type === 'newsStories') {
      existing.newsStories = new Date().toISOString();
    } else if (type === 'companyNews' && domain) {
      existing.companyNews = existing.companyNews || {};
      existing.companyNews[domain] = new Date().toISOString();
    }
    localStorage.setItem(CACHE_KEYS.LAST_REFRESHED, JSON.stringify(existing));
  } catch (e) {
    console.warn('Cache write failed:', e);
  }
};

export const getNewsStoriesLastRefreshed = () => {
  const { newsStories } = getLastRefreshed();
  return newsStories;
};

export const getCompanyNewsLastRefreshed = (domain) => {
  const { companyNews } = getLastRefreshed();
  return companyNews?.[domain] || null;
};

// ==================== PORTFOLIO COMPANIES (Derived) ====================

export const getPortfolioCompanies = () => {
  const allStories = getAllCachedStories();
  const companies = new Map();
  
  allStories.forEach(story => {
    if (story.portfolio_impact && Array.isArray(story.portfolio_impact)) {
      story.portfolio_impact.forEach(impact => {
        const name = impact.company_name;
        if (name) {
          if (!companies.has(name)) {
            companies.set(name, {
              name,
              storyCount: 0,
              highImpactCount: 0,
              latestStory: null,
            });
          }
          const company = companies.get(name);
          company.storyCount++;
          if (impact.scale === 'high') company.highImpactCount++;
          if (!company.latestStory || new Date(story.updated_at) > new Date(company.latestStory.updated_at)) {
            company.latestStory = story;
          }
        }
      });
    }
  });
  
  return Array.from(companies.values()).sort((a, b) => b.storyCount - a.storyCount);
};

// ==================== CONFIRMED WEBSITE (for API calls) ====================

const CONFIRMED_WEBSITE_PREFIX = 'wokelo_cw_';

export const getConfirmedWebsite = (permalink) => {
  try { return localStorage.getItem(CONFIRMED_WEBSITE_PREFIX + permalink) || null; }
  catch { return null; }
};

export const setConfirmedWebsite = (permalink, website) => {
  try { localStorage.setItem(CONFIRMED_WEBSITE_PREFIX + permalink, website); }
  catch {}
};

// ==================== REPORT ID STORAGE ====================

const REPORT_ID_PREFIX = 'wokelo_report_id_';

export const storeReportId = (permalink, reportId) => {
  const id = parseInt(reportId, 10);
  if (!id || isNaN(id)) return;
  try { localStorage.setItem(REPORT_ID_PREFIX + permalink, id.toString()); } catch {}
};

export const getStoredReportId = (permalink) => {
  // 1. Direct key
  try { const v = localStorage.getItem(REPORT_ID_PREFIX + permalink); const n = parseInt(v, 10); if (n && !isNaN(n)) return n; } catch {}
  // 2. From company news cache
  try { const v = localStorage.getItem('wokelo_company_news_' + permalink); if (v) { const e = JSON.parse(v); const n = parseInt(e?.reportId || e?.report_id, 10); if (n && !isNaN(n)) return n; } } catch {}
  // 3. From searched companies list
  try { const c = getSearchedCompany(permalink); const n = parseInt(c?.reportId || c?.report_id, 10); if (n && !isNaN(n)) return n; } catch {}
  return null;
};

// ==================== CLEAR CACHE ====================

export const clearAllCache = () => {
  Object.values(CACHE_KEYS).forEach(key => {
    localStorage.removeItem(key);
  });
};

export const clearNewsStoriesCache = () => {
  localStorage.removeItem(CACHE_KEYS.NEWS_STORIES);
  const refreshed = getLastRefreshed();
  refreshed.newsStories = null;
  localStorage.setItem(CACHE_KEYS.LAST_REFRESHED, JSON.stringify(refreshed));
};

export const clearCompanyNewsCache = (domain = null) => {
  if (domain) {
    const existing = getCompanyNewsCache();
    delete existing[domain];
    localStorage.setItem(CACHE_KEYS.COMPANY_NEWS, JSON.stringify(existing));
  } else {
    localStorage.removeItem(CACHE_KEYS.COMPANY_NEWS);
  }
};

// ==================== KEYWORD MATCHING (for searched companies) ====================

const STOPWORDS = new Set(['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'inc', 'corp', 'ltd', 'llc', 'co']);

export const getRecommendedStories = (companyName, maxResults = 3) => {
  const allStories = getAllCachedStories();
  const keywords = companyName
    .toLowerCase()
    .split(/\s+/)
    .filter(w => w.length > 2 && !STOPWORDS.has(w));
  
  const scored = allStories.map(story => {
    let score = 0;
    const title = (story.title || '').toLowerCase();
    const snippet = (story.snippet || '').toLowerCase();
    const tags = [...(story.freestyle_tags || []), ...(story.sectional_tags || [])].map(t => t.toLowerCase());
    
    keywords.forEach(kw => {
      if (title.includes(kw)) score += 10; // Title match = highest
      if (snippet.includes(kw)) score += 5; // Snippet match
      if (tags.some(t => t.includes(kw))) score += 3; // Tag match
    });
    
    // Exact name match in title is best
    if (title.includes(companyName.toLowerCase())) score += 20;
    
    return { story, score };
  });
  
  return scored
    .filter(s => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, maxResults)
    .map(s => s.story);
};

export default {
  getNewsStoriesCache,
  setNewsStoriesCache,
  getStoriesForDate,
  getAllCachedStories,
  getCompanyNewsCache,
  setCompanyNewsCache,
  getCompanyNews,
  getSearchedCompanies,
  getSearchedCompany,
  addSearchedCompany,
  updateSearchedCompanyStatus,
  clearSearchedCompanyReport,
  removeSearchedCompany,
  clearCompanyNewsCacheEntry,
  derivePermalink,
  getLastRefreshed,
  getNewsStoriesLastRefreshed,
  getCompanyNewsLastRefreshed,
  getPortfolioCompanies,
  getRecommendedStories,
  getRelativeTime,
  clearAllCache,
  clearNewsStoriesCache,
  clearCompanyNewsCache,
  getConfirmedWebsite,
  setConfirmedWebsite,
  storeReportId,
  getStoredReportId,
};
